//////////////////
// IMPORTS
//////////////////
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
// suggestion-action-colors imports
import { IContainer, IProps, IState } from "./SuggestionActionColors.d";
import { suggestionActionColorsFragments } from "./SuggestionActionColors.gql";
import * as s from "./SuggestionActionColors.styles";
// other components
import { CardButton } from "@components/card";
// state
import { IReduxState } from "@reducers/root.reducer";
import { setSettingsDialogOpen } from "@actions/chrome-extension.actions";
import { toggleExpanded } from "@actions/settings.actions";
import { snackBarOpen } from "@actions/snack-bar.actions";
import { updateEventColoringSettings } from "@mutations";
// util
import { defaultSet } from "@clockwise/client-commons/src/util/event-category-coloring";
import { track, TrackingEvents } from "@util/analytics.util";
// material-ui
import { Palette } from "@material-ui/icons";

//////////////////
// COMPONENT
//////////////////
class SuggestionActionColorsCmpt extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      saving: false,
      complete: this.isCompleteFromProps(props),
    };
  }

  // ~-~-~-~-~-~-~-
  // State Handling
  // ~-~-~-~-~-~-~-
  private isCompleteFromProps = (props: IProps) => {
    const settings = props.org && props.org.eventColoringSettingsErrorable;
    if (!settings || settings.__typename === "GraphEntityError") {
      return false;
    }
    return !!settings.eventCategoryColorings.find((setting) => setting.active);
  };

  public UNSAFE_componentWillReceiveProps(props: IProps) {
    if (this.state.complete) {
      return;
    }
    const complete = this.isCompleteFromProps(props);
    if (complete) {
      this.setState({ complete });
    }
  }

  // ~-~-~-~-~-~-~-
  // Click Handlers
  // ~-~-~-~-~-~-~-
  private onMutationSuccess = () => {
    this.setState({ saving: false, complete: true });
    track(TrackingEvents.COLOR_CATEGORIES.SETTINGS_ENABLED);
    this.props.snackBarOpen("Success! Color-coding started.");
  };

  private onMutationFailure = () => {
    this.setState({ saving: false });
    this.props.snackBarOpen("Sorry, something went wrong. Please try again.");
  };

  private onSave = () => {
    this.setState({ saving: true });
    const input = {
      orgRelayId: this.props.org.id,
      eventCategoryColorings: defaultSet,
    };
    updateEventColoringSettings(
      this.props.relay.environment,
      input,
      this.onMutationSuccess,
      this.onMutationFailure,
    );
  };

  private onOpenSettings = () => {
    this.props.setSettingsDialogOpen(true);
    this.props.toggleExpanded("expandColorCoding");
  };

  // ~-~-~-~-~-~-~-
  // Rendering
  // ~-~-~-~-~-~-~-
  // helper for assembling the buttons
  private renderBtn = (label: string, action: () => void, icon?: JSX.Element) => {
    const { saving } = this.state;
    const textColor = saving ? s.btnColorSaving : s.btnColor;
    return (
      <CardButton
        flat
        label={label}
        disabled={saving}
        textColor={textColor}
        onClick={() => action()}
        icon={icon}
        style={icon ? {} : { width: "inherit", display: "inline-block" }}
      />
    );
  };

  // primary rendering
  public render() {
    const { complete, saving } = this.state;

    if (!complete) {
      const label = saving ? "Saving..." : "Enable color-coding";
      return this.renderBtn(label, this.onSave, <Palette />);
    } else {
      return (
        <div>
          <div style={s.syncing}>Color-coding your events. This may take a few minutes.</div>
          {this.renderBtn("Done", this.props.actionCallback)}
          {this.renderBtn("Settings", this.onOpenSettings)}
        </div>
      );
    }
  }
}

//////////////////
// REDUX
//////////////////
function mapStateToProps(_state: IReduxState, _ownProps: IContainer) {
  return {};
}

const SuggestionActionColorsRedux = connect(mapStateToProps, {
  setSettingsDialogOpen,
  toggleExpanded,
  snackBarOpen,
})(SuggestionActionColorsCmpt);

//////////////////
// RELAY
//////////////////
export const SuggestionActionColors = createFragmentContainer<IContainer>(
  SuggestionActionColorsRedux,
  suggestionActionColorsFragments,
);
